import type React from "react";
import { forwardRef, useState } from "react";
import type { ColorProp, ColorTokens, FontSizeTokens, GetProps, SizeTokens, TamaguiElement } from "tamagui";
import { createStyledContext, getFontSize, isWeb, styled, Button as TButton, Input as TInput, TextArea as TTextArea, useGetThemedIcon, View, withStaticProperties, XGroup } from "tamagui";
import type { RadiusKeys } from "../../themes/token-radius";
import type { SpaceKeys } from "../../themes/token-space";
import { Paragraph } from "../Typography";

/** Extracts numeric size from a Tamagui font token (number or { val: number }) */
function getFontSizeValue(token: unknown): number | undefined {
  if (typeof token === "number") {
    return token;
  }
  if (token != null && typeof token === "object" && "val" in token) {
    const v = token.val;
    return typeof v === "number" ? v : undefined;
  }
  return undefined;
}

/** Safely gets a font token value by key (avoids unsafe member access on error-typed font) */
function getFontTokenByKey(font: unknown, key: string, tokenType: "size" | "lineHeight"): number | undefined {
  if (font == null || typeof font !== "object" || !(tokenType in font)) {
    return undefined;
  }
  const tokens = (font as Record<string, unknown>)[tokenType];
  if (tokens == null || typeof tokens !== "object" || !(key in tokens)) {
    return undefined;
  }
  const token = (tokens as Record<string, unknown>)[key];
  return getFontSizeValue(token);
}

/** Safely gets a font property value by key (avoids unsafe member access on error-typed font) */
function getFontProp(font: unknown, prop: string, key: string): string | number | undefined {
  if (font == null || typeof font !== "object" || !(prop in font)) {
    return undefined;
  }
  const obj = (font as Record<string, unknown>)[prop];
  if (obj == null || typeof obj !== "object" || !(key in obj)) {
    return undefined;
  }
  const token = (obj as Record<string, unknown>)[key];
  if (typeof token === "string" || typeof token === "number") {
    return token;
  }
  if (token != null && typeof token === "object" && "val" in token) {
    const v = token.val;
    if (typeof v === "string" || typeof v === "number") {
      return v;
    }
  }
  return undefined;
}
const defaultContextValues = {
  size: "$4",
  color: undefined
} as const;
const InputContext = createStyledContext<{
  size: FontSizeTokens;
  color?: ColorTokens;
}>(defaultContextValues);
const defaultInputGroupStyles = {
  size: "$4",
  fontFamily: "$body",
  color: "$color",
  borderWidth: 1,
  margin: 1,
  outlineWidth: 0,
  ...(isWeb ? {
    tabIndex: -1
  } : {
    focusable: true
  }),
  borderColor: "$borderColor",
  backgroundColor: "$background",
  placeholderTextColor: "$textSubtle",
  hoverStyle: {
    borderColor: "$borderColorHover"
  },
  disabledStyle: {
    borderColor: "$borderStrong",
    backgroundColor: "$bgDisabledSubtle",
    color: "$textSubtle",
    disabled: true
  },
  focusStyle: {
    borderColor: "$borderColorFocus",
    borderWidth: 2,
    margin: 0,
    // Keep focus ring color stable while hovered.
    hoverStyle: {
      borderColor: "$borderColorFocus"
    }
  }
} as const;
const InputGroupFrame = styled(XGroup, {
  justifyContent: "space-between",
  context: InputContext,
  variants: {
    unstyled: {
      false: defaultInputGroupStyles
    },
    tone: {
      default: {},
      negative: {
        borderColor: "$borderNegative",
        hoverStyle: {
          borderColor: "$borderNegativeHover"
        }
      }
    },
    focused: {
      true: defaultInputGroupStyles.focusStyle
    },
    size: {
      "...size": () => {
        return {
          borderRadius: "$4"
        };
      }
    },
    disabled: {
      true: defaultInputGroupStyles.disabledStyle,
      false: {}
    }
  } as const,
  defaultVariants: {
    unstyled: process.env.TAMAGUI_HEADLESS === "1",
    tone: "default"
  }
});
const FocusContext = createStyledContext({
  setFocused: (_val: boolean) => {},
  focused: false
});
const InputGroupImpl = forwardRef<TamaguiElement, GetProps<typeof InputGroupFrame>>((props, forwardedRef) => {
  const {
    children,
    tone,
    ...rest
  } = props;
  const [focused, setFocused] = useState(false);

  // The `focused` variant is manually applied (not a real :focus pseudo) since
  // focus lives on the inner TInput, not the XGroup. That means tone's own
  // focusStyle can't override the focused-variant's borderColor. Apply the
  // focused+negative override inline so it wins via declaration order.
  const focusedNegativeOverride = focused && tone === "negative" ? {
    borderColor: "$borderNegativeStrong",
    hoverStyle: {
      borderColor: "$borderNegativeStrong"
    }
  } as const : null;
  return <FocusContext.Provider scope="" focused={focused} setFocused={setFocused}>
      <InputGroupFrame ref={forwardedRef} {...rest} tone={tone} focused={focused} {...focusedNegativeOverride}>
        {children}
      </InputGroupFrame>
    </FocusContext.Provider>;
});
InputGroupImpl.displayName = "InputGroupImpl";
const inputFrameVariants = {
  hasLabel: {
    true: {
      paddingTop: 16
    },
    false: {
      paddingTop: 0
    }
  }
} as const;
const InputFrame = styled(TInput, {
  unstyled: true,
  fontFamily: "$body",
  width: "100%",
  f: 1,
  selectionColor: "$bgBrand",
  context: InputContext,
  variants: inputFrameVariants,
  defaultVariants: {
    hasLabel: false
  }
});
const TextAreaFrame = styled(TTextArea, {
  unstyled: true,
  fontFamily: "$body",
  width: "100%",
  f: 1,
  selectionColor: "$bgBrand",
  context: InputContext,
  variants: inputFrameVariants,
  defaultVariants: {
    hasLabel: false
  }
});
const InputImpl = InputFrame.styleable<GetProps<typeof InputFrame>>((props, ref) => {
  const {
    setFocused
  } = FocusContext.useStyledContext();
  const {
    size
  } = InputContext.useStyledContext();
  const {
    disabled,
    onBlur,
    height: _height,
    multiline,
    hasLabel,
    secureTextEntry,
    ...rest
  } = props;
  const handleFocus = () => {
    setFocused(true);
  };
  const handleBlur: typeof onBlur = e => {
    if (onBlur) {
      onBlur(e);
    }
    setFocused(false);
  };

  // Tamagui v2 dropped the secureTextEntry → type="password" mapping on web.
  const webPasswordProps = isWeb && secureTextEntry ? {
    type: "password" as const
  } : {};
  if (multiline) {
    return <TextAreaFrame ref={ref} onFocus={handleFocus} onBlur={handleBlur} size={size as SizeTokens} disabled={disabled} hasLabel={hasLabel} secureTextEntry={secureTextEntry} {...rest} />;
  }
  return <InputFrame ref={ref} onFocus={handleFocus} onBlur={handleBlur} size={size as SizeTokens} disabled={disabled} height={54} hasLabel={hasLabel} secureTextEntry={secureTextEntry} {...webPasswordProps} {...rest} />;
});
const InputSection = styled(XGroup.Item, {
  justifyContent: "center",
  alignItems: "center",
  context: InputContext
});
const Button = styled(TButton, {
  context: InputContext,
  justifyContent: "center",
  alignItems: "center",
  variants: {
    size: {
      "...size": (val = "$true", {
        tokens
      }) => {
        if (typeof val === "number") {
          return {
            paddingHorizontal: 0,
            height: val,
            borderRadius: val * 0.2
          };
        }
        return {
          paddingHorizontal: 0,
          height: val,
          borderRadius: tokens.radius[val as RadiusKeys]
        };
      }
    }
  } as const
});

// Icon starts

const InputIconFrame = styled(View, {
  justifyContent: "center",
  alignItems: "center",
  context: InputContext,
  variants: {
    size: {
      "...size": (val, {
        tokens
      }) => {
        return {
          paddingHorizontal: tokens.space[val as SpaceKeys]
        };
      }
    }
  } as const
});
const getIconSize = (size: FontSizeTokens, scale: number) => {
  return (typeof size === "number" ? size * 0.5 : getFontSize(size as FontSizeTokens)) * scale;
};
const InputIcon = InputIconFrame.styleable<{
  scaleIcon?: number;
  color?: ColorTokens;
}>((props, ref: React.Ref<TamaguiElement>) => {
  const {
    children,
    scaleIcon = 1.2,
    color: colorProp,
    ...rest
  } = props;
  const inputContext = InputContext.useStyledContext();
  const focusContext = FocusContext.useStyledContext();
  const {
    size = "$true"
  } = inputContext;
  const color = colorProp ?? (focusContext.focused ? "$borderIntense" : "$borderDefault");
  const iconSize = getIconSize(size, scaleIcon);
  const getThemedIcon = useGetThemedIcon({
    size: iconSize,
    color: color as ColorProp
  });
  return <InputIconFrame paddingRight="$2" paddingLeft="$3.5" ref={ref} {...rest}>
      {getThemedIcon(children)}
    </InputIconFrame>;
});
const InputContainerFrame = styled(View, {
  context: InputContext,
  flexDirection: "column",
  position: "relative",
  variants: {
    size: {
      "...size": () => {
        return {};
        // Removed from default Bento, since we achieve exact spacing with label
        // padding instead.

        // return { gap: tokens.space[val as SpaceKeys].val * 0.2 };
      }
    },
    color: {
      "...color": () => ({})
    }
    // gapScale: {
    //   ":number": {} as any,
    // },
  } as const,
  defaultVariants: {
    size: "$4"
  }
});
const InputLabel = styled(Paragraph, {
  context: InputContext,
  fontFamily: "$body",
  position: "absolute",
  zIndex: 1,
  left: 18,
  top: 8,
  fontWeight: "400",
  variants: {
    size: {
      "...size": (val, {
        font
      }) => {
        // Downsize the label one size from the input font size.
        const size = parseInt(val.toString().replace("$", ""), 10);
        if (size > 0) {
          const key = `$${size - 2}`;
          return {
            fontSize: getFontTokenByKey(font, key, "size") ?? "$3",
            lineHeight: getFontTokenByKey(font, key, "lineHeight") ?? "$3"
          };
        }
        return {
          fontSize: val,
          lineHeight: val
        };
      }
    }
  } as const,
  defaultVariants: {
    size: "$2"
  }
});
const InputXGroup = styled(XGroup, {
  context: InputContext,
  variants: {
    size: {
      "...size": (val, {
        tokens
      }) => {
        const radiusToken = tokens.radius[val as RadiusKeys] ?? tokens.radius.true;
        return {
          borderRadius: radiusToken
        };
      }
    }
  } as const
});
const InputInfo = styled(Paragraph, {
  context: InputContext,
  variants: {
    size: {
      "...fontSize": (val, {
        font
      }) => {
        if (!font) {
          return;
        }
        const key = String(val);
        const fontSize = getFontTokenByKey(font, key, "size") ?? 0;
        const lineHeight = (getFontTokenByKey(font, key, "lineHeight") ?? 0) * 0.8;
        const fontWeight = getFontProp(font, "weight", "$2");
        const letterSpacing = getFontProp(font, "letterSpacing", key);
        const textTransform = getFontProp(font, "transform", key);
        const fontStyle = getFontProp(font, "style", key);
        return {
          fontSize,
          lineHeight,
          fontWeight,
          letterSpacing,
          textTransform,
          fontStyle
        };
      }
    }
  } as const
});
export const BentoInput = withStaticProperties(InputContainerFrame, {
  Box: InputGroupImpl,
  Area: InputImpl,
  Section: InputSection,
  Button,
  Icon: InputIcon,
  Info: InputInfo,
  Label: InputLabel,
  XGroup: withStaticProperties(InputXGroup, {
    Item: XGroup.Item
  })
});